import { Link, useParams } from 'react-router';
import { Accordion } from '@/components/UI/Accordion';
import { AvatarGroup } from '@/components/UI/Avatar/AvatarGroup';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import { Skeleton } from '@/components/UI/Skeleton';
import { ImpactStats } from '@/features/ChangeOrder/Summary/ImpactStats';
import { useChangeOrderSummary } from '@/hooks/ReactQueryHooks/useChangeOrderSummaryQuery';
import { getInitials } from '@/utils';
import { ChangeOrderBudget as Budget } from './ChangeOrderBudget';
import { ChangeOrderDeliverables as Deliverables } from './ChangeOrderDeliverables';
import { ChangeOrderMilestones as Milestones } from './ChangeOrderMilestones';

export function ChangeOrderSummary() {
  const { projectId = '' } = useParams();

  const summaryQuery = useChangeOrderSummary({ projectId });

  const approverOptions =
    summaryQuery.data?.uniqueApprovers.map((approver) => ({
      label: approver.name,
      value: String(approver.id),
    })) || [];

  const submitterOptions =
    summaryQuery.data?.uniqueSubmitters.map((submitter) => ({
      label: submitter.name,
      value: String(submitter.id),
    })) || [];

  return (
    <div>
      <div className='flex justify-between bg-white rounded-md shadow-lg p-6'>
        <div>
          <p className='text-lg mb-2'>
            <Skeleton loading={summaryQuery.isLoading}>{summaryQuery.data?.project?.name} Change Order Summary</Skeleton>
          </p>
          {summaryQuery.isSuccess && (
            <Link
              to={`/app/administration/users/${summaryQuery.data?.project?.projectLead?.id}`}
              className='flex items-center gap-2 hover:bg-gray-50 transition-colors p-2 -mx-2 rounded-xl'
            >
              <ImageCircularAvatar
                customClass='h-16 w-16 mr-1'
                imageUrl={summaryQuery.data?.project?.projectLead.profileImage || ''}
                fallbackText={getInitials(summaryQuery.data?.project?.projectLead?.name || '')}
              />
              <div>
                <p className='font-bold text-lg'>{summaryQuery.data?.project?.projectLead.name}</p>
                <p className='text-neutralColor-normal'>Project Lead</p>
              </div>
            </Link>
          )}

          <div className='flex items-center gap-2 mt-2'>
            <span className='text-base'>C/O Approvers: </span>{' '}
            <AvatarGroup
              items={summaryQuery.data?.uniqueApprovers?.map((approver) => ({
                image: approver.profileImage,
                name: approver.name,
              }))}
            />
          </div>
        </div>

        <div className='space-y-2'>
          <ImpactStats label='Milestones Impact' value={summaryQuery.data?.changeOrderMilestoneImpacted || 0} type='days' />
          <ImpactStats label='Deliverables Impact' value={summaryQuery.data?.changeOrderDeliverableImpacted || 0} />
          <ImpactStats label='Budget Impact' value={summaryQuery.data?.budgetImpacted || 0} type='money' />
        </div>
      </div>

      <div className='mt-5'>
        <Accordion multi>
          <div className='space-y-5'>
            <Milestones
              submitterOptions={submitterOptions}
              approverOptions={approverOptions}
              totalImpact={summaryQuery.data?.changeOrderMilestoneImpacted || 0}
            />
            <Deliverables
              submitterOptions={submitterOptions}
              approverOptions={approverOptions}
              totalImpact={summaryQuery.data?.changeOrderDeliverableImpacted || 0}
            />
            <Budget
              submitterOptions={submitterOptions}
              approverOptions={approverOptions}
              totalImpact={summaryQuery.data?.budgetImpacted || 0}
            />
          </div>
        </Accordion>
      </div>
    </div>
  );
}
